/**
 * MARS Portfolio API surface.
 *
 * Thin wrappers over `apiGet` that return typed responses. Every fetcher
 * returns a Promise suitable for `useQuery({ queryFn })` or
 * `queryClient.prefetchQuery`.
 *
 * URLs are produced by the typed registry in `lib/api/endpoints.ts`. To
 * change a backend route, update the registry — every caller picks up the
 * new URL automatically.
 */
import { apiGet } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import type {
  FundId,
  FundsResponse,
  MetricType,
  PerformanceResponse,
  RiskMetricsResponse,
  RiskPeriod,
  Timeframe,
  TimeseriesResponse,
} from './types';

export interface ToggleParams {
  vol_timing?: boolean;
  constraints?: boolean;
  tc?: boolean;
}

export function fetchFunds(): Promise<FundsResponse> {
  return apiGet<FundsResponse>(endpoints.mars.portfolioFunds());
}

export function fetchTimeseries(fund: FundId): Promise<TimeseriesResponse> {
  return apiGet<TimeseriesResponse>(endpoints.mars.portfolioTimeseries(fund));
}

export function fetchRiskMetrics(
  fund: FundId,
  metricType: MetricType,
  period: RiskPeriod = '3y',
  toggles: ToggleParams = {},
): Promise<RiskMetricsResponse> {
  return apiGet<RiskMetricsResponse>(
    endpoints.mars.portfolioRiskMetrics(fund, {
      type: metricType,
      timeframe: period,
      ...toggles,
    }),
  );
}

export function fetchPerformance(
  fund: FundId,
  startDate?: string,
  endDate?: string,
): Promise<PerformanceResponse> {
  return apiGet<PerformanceResponse>(
    endpoints.mars.portfolioPerformance(fund, {
      start_date: startDate,
      end_date: endDate,
    }),
  );
}

/** React Query keys. Centralised here so invalidation calls stay in sync. */
export const marsPortfolioKeys = {
  funds: () => ['mars-portfolio', 'funds'] as const,
  timeseries: (fund: FundId) => ['mars-portfolio', 'timeseries', fund] as const,
  exposures: (fund: FundId, toggles: ToggleParams, timeframe: Timeframe) =>
    ['mars-portfolio', 'exposures', fund, toggles, timeframe] as const,
  riskMetrics: (fund: FundId, metric: MetricType, period: RiskPeriod, toggles: ToggleParams) =>
    ['mars-portfolio', 'risk-metrics', fund, metric, period, toggles] as const,
  performance: (fund: FundId, startDate?: string, endDate?: string) =>
    ['mars-portfolio', 'performance', fund, startDate ?? null, endDate ?? null] as const,
};
